import { Navigate, useParams, useRoutes } from 'react-router-dom';
// auth
import AuthGuard from '../auth/AuthGuard';

import CompactLayout from '../layouts/compact';
import DashboardLayout from 'src/layouts/app/DashboardLayout';
import RegistrationLayout from 'src/layouts/signUp/RegistrationLayout';

import {
  AdminPage,
  ComingSoonPage,
  HomePage,
  ListEditPage,
  ListsPage,
  DataPage,
  MaintenancePage,
  OrganizationSettingsPage,
  Page403,
  Page404,
  Page500,
  ProjectsPage,
  ReportsPage,
  MessagesPage,
  SurveyBuilderPage,
  TeamPage,
  ConditionalSurveyEditPage,
  ConditionalTextEditPage,
  RelationalProofPage,
  RelationalProofSuccessPage,
  SignUpWizardPage,
  SignUpWizardSuccessPage,
  TextEditPage,
  VoicemailEditPage,
  OrganizationCreationPage,
  OrganizationUpdatePage,
  RegistrationPage,
  NoOrganizationPage,
  TextConversationsPage,
  VFUpdatesPage,
  CampaignsPage,
  CanvassingQAPage,
  CrmContactsPage,
} from './elements';

import RoleBasedGuard from 'src/auth/RoleBasedGuard';
import { Features } from 'src/auth/fbac';
import BillingGuard from 'src/sections/main/billing/BillingGuard';
import RelationalLayout from 'src/layouts/relational/RelationalLayout';
import { ProjectSSEProvider } from 'src/pages/main/projects/ProjectSSEProvider';
import SignUpWizardLayout from 'src/layouts/signUp/SignUpWizardLayout';
import DtTermsGuard from 'src/components/terms/DtTermsGuard';

function LegacySignUpRedirect() {
  const { discountOrg } = useParams();
  const target = discountOrg
    ? `/get-started?parent_org=${encodeURIComponent(discountOrg)}`
    : '/get-started';

  return <Navigate to={target} replace />;
}

export default function Router() {
  return useRoutes([
    // Dashboard
    {
      element: (
        <AuthGuard>
          <BillingGuard>
            <DtTermsGuard>
              <DashboardLayout />
            </DtTermsGuard>
          </BillingGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/home" replace />, index: true },
        { path: 'home', element: <RoleBasedGuard path="/home" element={<HomePage />} /> },
        {
          path: 'projects',
          element: (
            <RoleBasedGuard
              path="/projects"
              element={
                <ProjectSSEProvider>
                  <ProjectsPage />
                </ProjectSSEProvider>
              }
            />
          ),
        },
        { path: 'reports', element: <RoleBasedGuard path="/reports" element={<ReportsPage />} /> },
        {
          path: 'canvassing',
          element: (
            <RoleBasedGuard
              path="/canvassing"
              features={[Features.CanvassingQA]}
              element={<CanvassingQAPage />}
            />
          ),
        },
        { path: 'admin', element: <RoleBasedGuard path="/admin" element={<AdminPage />} /> },
        { path: 'team', element: <RoleBasedGuard path="/team" element={<TeamPage />} /> },
        {
          path: 'lists',
          children: [
            { element: <RoleBasedGuard path="/lists" element={<ListsPage />} />, index: true },
            {
              path: ':id',
              element: <RoleBasedGuard path="/lists/:id" element={<ListEditPage />} />,
            },
          ],
        },
        {
          path: 'crm/contacts',
          element: (
            <RoleBasedGuard
              path="/crm/contacts"
              features={[Features.Crm]}
              element={<CrmContactsPage />}
            />
          ),
        },
        {
          path: 'settings',
          children: [
            {
              element: <RoleBasedGuard path="/settings" element={<OrganizationSettingsPage />} />,
              index: true,
            },
            {
              path: 'organization/creation',
              element: (
                <RoleBasedGuard
                  path="/settings/organization/creation"
                  element={<OrganizationCreationPage />}
                />
              ),
            },
            {
              path: 'organization/update',
              element: (
                <RoleBasedGuard
                  path="/settings/organization/update"
                  element={<OrganizationUpdatePage />}
                />
              ),
            },
            {
              path: 'vf-updates',
              element: <RoleBasedGuard path="/settings/vf-updates" element={<VFUpdatesPage />} />,
            },
          ],
        },
        {
          path: 'campaigns',
          element: (
            <RoleBasedGuard
              path="/campaigns"
              features={[Features.Campaigns]}
              element={<CampaignsPage />}
            />
          ),
        },
        {
          path: 'conversations',
          element: <RoleBasedGuard path="/conversations" element={<TextConversationsPage />} />,
        },
        {
          path: 'data',
          element: <RoleBasedGuard path="/data" element={<DataPage />} />,
        },
        {
          path: 'messaging',
          children: [
            {
              element: <RoleBasedGuard path="/messaging" element={<MessagesPage />} />,
              index: true,
            },
            {
              path: 'surveys',
              children: [
                {
                  path: ':id',
                  element: (
                    <RoleBasedGuard path="/messaging/surveys/:id" element={<SurveyBuilderPage />} />
                  ),
                },
              ],
            },
            {
              path: 'conditional-surveys',
              children: [
                {
                  path: ':id',
                  element: (
                    <RoleBasedGuard
                      path="/messaging/conditional-surveys/:id"
                      element={<ConditionalSurveyEditPage />}
                    />
                  ),
                },
              ],
            },
            {
              path: 'texts',
              children: [
                {
                  path: ':id',
                  element: (
                    <RoleBasedGuard path="/messaging/texts/:id" element={<TextEditPage />} />
                  ),
                },
              ],
            },
            {
              path: 'conditional-texts',
              children: [
                {
                  path: ':id',
                  element: (
                    <RoleBasedGuard
                      path="/messaging/conditional-texts/:id"
                      element={<ConditionalTextEditPage />}
                    />
                  ),
                },
              ],
            },
            {
              path: 'voicemails',
              children: [
                {
                  path: ':id',
                  element: (
                    <RoleBasedGuard
                      path="/messaging/voicemails/:id"
                      element={<VoicemailEditPage />}
                    />
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
    { path: 'sign-up', element: <Navigate to="/get-started" replace /> },
    { path: 'sign-up/:discountOrg', element: <LegacySignUpRedirect /> },
    { path: 'sign-up/success', element: <Navigate to="/get-started/success" replace /> },
    { path: 'signup', element: <Navigate to="/get-started" replace /> },
    { path: 'signup/:discountOrg', element: <LegacySignUpRedirect /> },
    { path: 'signup/success', element: <Navigate to="/get-started/success" replace /> },
    {
      element: <SignUpWizardLayout />,
      children: [
        { path: 'get-started', element: <SignUpWizardPage /> },
        { path: 'get-started/success', element: <SignUpWizardSuccessPage /> },
      ],
    },
    {
      element: <RegistrationLayout />,
      children: [{ path: 'registration', element: <RegistrationPage /> }],
    },
    {
      element: <RelationalLayout />,
      children: [
        { path: 'relational-proof', element: <RelationalProofPage /> },
        { path: 'relational-proof/success', element: <RelationalProofSuccessPage /> },
      ],
    },
    {
      element: <RegistrationLayout />,
      children: [{ path: 'organization-pending', element: <NoOrganizationPage /> }],
    },
    {
      element: <CompactLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoonPage /> },
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
